import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {addResultCases, stripUndefined} from 'utils/helpers';
import ApiService from 'redux/services/api-service';
import {ListResult, SingleResult} from 'utils/types';

export interface CampaignMediaImageApiData {
  id: number;
  campaign: number;
  image_url: string | null;
  image_hash: string | null;
  uploaded_by: number;
  uploaded_at: string;
}

export interface CampaignIntakeApiData {
  id?: number;
  campaign: number | string | undefined;
  customer_io: string;
  manufacturer: string;
  brand_name: string;
  start_date: string;
  end_date: string;
  impressions: number;
  budget: number;
  has_new_items: boolean;
  new_items_details: string;
  has_other_studies: boolean;
  other_studies_details: string;
  retailer: number | null;
  co_branded_campaign_info: string;
  retailer_comments: string;
  test_control_notes: string;
  measurement_notes: string;
  kpi: string[];
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  hero_upc_items: {
    upc: string;
    description: string;
    attributes: Record<string, string>;
  }[];
  halo_upc_items: {
    upc: string;
    description: string;
    attributes: Record<string, string>;
  }[];
}

export interface IState {
  campaignMediaImages: ListResult<CampaignMediaImageApiData>;
  newCampaignOptions: SingleResult<{
    kpis: {value: string; label: string}[];
    method: {value: string; label: string}[];
    retailers: {value: string; label: string}[];
    categories: {value: number; label: string; description: string; is_active: boolean}[];
  }>;
  campaignIntake: SingleResult<CampaignIntakeApiData>;
  upcItemsCampaignIntake: SingleResult<{
    hero_upc_items: {upc: string; description: string; attributes?: Record<string, string>}[];
    halo_upc_items: {upc: string; description: string; attributes?: Record<string, string>}[];
  }>;
  testControlNotesCampaignIntake: SingleResult<{test_control_notes: string}>;
}

export const campaignClientFormEndpoints = {
  updateCampaign: createAsyncThunk('campaignClientForm/updateCampaign', async (payload: any, {rejectWithValue}) => {
    try {
      const response = await ApiService.patch(`/campaigns/${payload?.id}/`, payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  }),
  updateCampaignSalesDataset: createAsyncThunk(
    'campaignClientForm/updateCampaignSalesDataset',
    async (payload: any, {rejectWithValue}) => {
      try {
        const response = await ApiService.patch(`/campaigns/${payload?.id}/update_sales_dataset/`, payload);
        return response.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  createCampaign: createAsyncThunk('campaignClientForm/createCampaign', async (payload: any) => {
    const response = await ApiService.post('/campaigns/', payload);
    return response.data;
  }),
  getNewCampaignOptionsList: createAsyncThunk('campaignClientForm/getNewCampaignOptionsList', async () => {
    const response = await ApiService.get('/new_campaign_options_list/');
    return {as: 'success' as const, value: response.data};
  }),
  createCampaignIntake: createAsyncThunk('campaignClientForm/createCampaignIntake', async (payload: any) => {
    const response = await ApiService.post('/campaign_intake_form/', payload);
    return response.data;
  }),
  updateCampaignIntake: createAsyncThunk('campaignClientForm/updateCampaignIntake', async (payload: any) => {
    const response = await ApiService.patch(`/campaign_intake_form/${payload.id}/`, payload);
    return response.data;
  }),
  getCampaignIntake: createAsyncThunk('campaignClientForm/getCampaignIntake', async (campaignIntakeFormId: any) => {
    const response = await ApiService.get(`/campaign_intake_form/${campaignIntakeFormId}/`);
    return {as: 'success' as const, value: response.data};
  }),
  getCampaignIntakeByCampaignId: createAsyncThunk(
    'campaignClientForm/getCampaignIntakeByCampaignId',
    async (campaignId: any) => {
      const response = await ApiService.get(`/campaign_intake_form/by_campaign_id/${campaignId}/`);
      return {as: 'success' as const, value: response.data};
    },
  ),
  getUpcItemsCampaignIntakeByCampaignId: createAsyncThunk(
    'campaignClientForm/getUpcItemsCampaignIntakeByCampaignId',
    async (campaignId: any) => {
      const response = await ApiService.get(`/campaign_intake_form/upc_items_by_campaign_id/${campaignId}/`);
      return {as: 'success' as const, value: response.data};
    },
  ),
  getTestControlNotesCampaignIntakeByCampaignId: createAsyncThunk(
    'campaignClientForm/getTestControlNotesCampaignIntakeByCampaignId',
    async (campaignId: any) => {
      const response = await ApiService.get(`/campaign_intake_form/test_control_notes_by_campaign_id/${campaignId}/`);
      return {as: 'success' as const, value: response.data};
    },
  ),
  getCampaignMediaImages: createAsyncThunk(
    'campaignClientForm/getCampaignMediaImages',
    async (campaignId: number | string) => {
      const response = await ApiService.get(`/campaign_media_image/?campaign_id=${campaignId}`);
      return response.data;
    },
  ),
  uploadCampaignMediaImage: createAsyncThunk(
    'campaignClientForm/uploadCampaignMediaImage',
    async (payload: {campaignId: number | string; image: File}, {rejectWithValue}) => {
      try {
        const formData = new FormData();
        formData.append('campaign', String(payload.campaignId));
        formData.append('image', payload.image);
        await ApiService.post('/campaign_media_image/', formData, {
          headers: {'Content-Type': 'multipart/form-data'},
        });
        const listResponse = await ApiService.get(`/campaign_media_image/?campaign_id=${payload.campaignId}`);
        return listResponse.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  deleteCampaignMediaImage: createAsyncThunk(
    'campaignClientForm/deleteCampaignMediaImage',
    async (payload: {id: number; campaignId: number | string}, {rejectWithValue}) => {
      try {
        await ApiService.delete(`/campaign_media_image/${payload.id}/`);
        const listResponse = await ApiService.get(`/campaign_media_image/?campaign_id=${payload.campaignId}`);
        return listResponse.data;
      } catch (err: any) {
        return rejectWithValue(err.response.data);
      }
    },
  ),
  makeMainCampaign: createAsyncThunk('campaignClientForm/makeMainCampaign', async (payload: any) => {
    const response = await ApiService.post('/campaigns/make_main_campaign/', payload);
    return response.data;
  }),
  getClientList: createAsyncThunk('campaignClientForm/getClientList', async (search: string) => {
    const response = await ApiService.get(`/client/?search=${search}`);
    return response.data;
  }),
  getClientContactList: createAsyncThunk(
    'campaignClientForm/getClientContactList',
    async (params: Record<string, any>) => {
      const response = await ApiService.get(`/client_contacts/?${new URLSearchParams(stripUndefined(params))}`);
      return response.data;
    },
  ),
  getMediaPartnerList: createAsyncThunk('campaignClientForm/getMediaPartnerList', async (search: string) => {
    const response = await ApiService.get(`/media_partner/?search=${search}`);
    return response.data;
  }),
  getDashboardBrandList: createAsyncThunk('campaignClientForm/getBrandList', async (search: string) => {
    const response = await ApiService.get(`/dashboard_brand/?search=${search}`);
    return response.data;
  }),
  createPublicCampaign: createAsyncThunk('campaignClientForm/createPublicCampaign', async (payload: any) => {
    const response = await ApiService.post('/public_rfp_form/', payload);
    return response.data;
  }),
};

export const campaignClientFormSlice = createSlice({
  name: 'campaignClientForm',
  initialState: {
    campaignMediaImages: {as: 'idle'},
    newCampaignOptions: {as: 'idle'},
    campaignIntake: {as: 'idle'},
    upcItemsCampaignIntake: {as: 'idle'},
    testControlNotesCampaignIntake: {as: 'idle'},
  } as IState,
  reducers: {},
  extraReducers: builder => {
    addResultCases(builder, campaignClientFormEndpoints.getCampaignMediaImages, 'campaignMediaImages');
    addResultCases(builder, campaignClientFormEndpoints.uploadCampaignMediaImage, 'campaignMediaImages');
    addResultCases(builder, campaignClientFormEndpoints.deleteCampaignMediaImage, 'campaignMediaImages');
    addResultCases(builder, campaignClientFormEndpoints.getNewCampaignOptionsList, 'newCampaignOptions');
    addResultCases(builder, campaignClientFormEndpoints.getCampaignIntake, 'campaignIntake');
    addResultCases(builder, campaignClientFormEndpoints.getCampaignIntakeByCampaignId, 'campaignIntake');
    addResultCases(
      builder,
      campaignClientFormEndpoints.getUpcItemsCampaignIntakeByCampaignId,
      'upcItemsCampaignIntake',
    );
    addResultCases(
      builder,
      campaignClientFormEndpoints.getTestControlNotesCampaignIntakeByCampaignId,
      'testControlNotesCampaignIntake',
    );
  },
});

export const campaignClientFormActions = campaignClientFormSlice.actions;

export type CampaignClientFormSlice = {
  [campaignClientFormSlice.name]: ReturnType<(typeof campaignClientFormSlice)['reducer']>;
};
